import axios from "axios";
import type {
  AxiosResponse,
  AxiosRequestConfig,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosInstance,
} from "axios";
import CryptoJS from "crypto-js";

import router from "@/router";
import toaster from "@/utils/toaster";
import { useToken } from "@/hooks/useToken";
import { useAuthStore } from "@/stores/auth";
import { HttpStatus } from "@/types/axios/httpStatus";
import type {
  ResponseModel,
  UploadFileItemModel,
  UploadRequestConfig,
} from "@/types/axios/model";

interface CustomRequestConfig extends AxiosRequestConfig {
  encrypt?: boolean;
}

class HttpRequest {
  service: AxiosInstance;
  private ENCRYPTION_KEY: string;

  constructor() {
    this.ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY || "";
    this.service = axios.create({
      baseURL: "/tenant-console-api",
      timeout: 30 * 1000,
      withCredentials: true,
    });

    this.service.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const { getToken } = useToken();
        const token = getToken();
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        config.headers["Access-Control-Allow-Origin"] = "*";
        const existingContentType =
          config.headers["Content-Type"] || config.headers["content-type"];
        if (!existingContentType) {
          config.headers["Content-Type"] = "application/json;charset=utf-8";
        }

        const customConfig = config as unknown as CustomRequestConfig;
        const normalizedContentType = String(
          config.headers["Content-Type"] || config.headers["content-type"] || "",
        ).toLowerCase();
        if (
          customConfig.encrypt &&
          config.data &&
          !normalizedContentType.includes("multipart/form-data") &&
          this.ENCRYPTION_KEY
        ) {
          const { encryptedData, iv } = this.encryptData(config.data);
          config.data = { data: encryptedData, iv };
        }

        return config;
      },
      (error: AxiosError) => Promise.reject(error),
      {
        synchronous: true,
        runWhen: (_config: InternalAxiosRequestConfig) => true,
      },
    );

    this.service.interceptors.response.use(
      (response: AxiosResponse<ResponseModel>): AxiosResponse["data"] => {
        if (response.config.responseType === "blob" || response.data instanceof Blob) {
          return response.data as unknown as AxiosResponse["data"];
        }

        const { data } = response;
        if (data.iv && data.data) {
          try {
            const decryptedStr = this.decryptData(data.data as unknown as string, data.iv);
            data.data = JSON.parse(decryptedStr);
          } catch (error) {
            console.error("Response decryption failed:", error);
            data.data = null as never;
            data.message = "Data decryption failed";
            data.status = HttpStatus.HTTP_500_INTERNAL_SERVER_ERROR;
          }
        }

        const { status } = data;
        if (status) {
          if (status !== HttpStatus.HTTP_200_OK) {
            switch (status) {
              case HttpStatus.HTTP_401_UNAUTHORIZED:
                this.handleUnauthorized("您的登录状态已过期，请重新登录");
                break;
              case HttpStatus.HTTP_403_FORBIDDEN:
                toaster.error(data.message || "您没有权限访问该资源");
                break;
              case HttpStatus.HTTP_404_NOT_FOUND:
                toaster.error(data.message || "资源不存在");
                break;
              case HttpStatus.HTTP_400_BAD_REQUEST:
                toaster.error(data.message || "请求参数错误");
                break;
              default:
                toaster.error(data.message || "操作失败");
                break;
            }
          }
          return data;
        }

        return Promise.reject("Error! code missing!");
      },
      (error: AxiosError) => {
        if (error.response) {
          const { status } = error.response;
          if (status === HttpStatus.HTTP_401_UNAUTHORIZED) {
            this.handleUnauthorized("您的登录状态已过期，请重新登录");
          }
        }
        return Promise.reject(error);
      },
    );
  }

  private handleUnauthorized(message: string) {
    const { removeToken } = useToken();
    const authStore = useAuthStore();
    removeToken();
    authStore.clearAuthContext();
    toaster.error(message);
    setTimeout(() => {
      if (router.currentRoute.value.path !== "/login") {
        window.location.href = "/login";
      }
    }, 1200);
  }

  private encryptData(data: unknown): { encryptedData: string; iv: string } {
    const iv = CryptoJS.lib.WordArray.random(16);
    const ivBase64 = CryptoJS.enc.Base64.stringify(iv);

    let key = this.ENCRYPTION_KEY;
    if (key.length < 32) {
      key = key.padEnd(32, "\0");
    }
    key = key.slice(0, 32);
    const keyWordArray = CryptoJS.enc.Utf8.parse(key);

    const jsonStr = typeof data === "object" ? JSON.stringify(data) : String(data);
    const encrypted = CryptoJS.AES.encrypt(jsonStr, keyWordArray, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return { encryptedData: encrypted.toString(), iv: ivBase64 };
  }

  private decryptData(encryptedData: string, iv: string): string {
    const ciphertext = CryptoJS.enc.Base64.parse(encryptedData);
    const ivWordArray = CryptoJS.enc.Base64.parse(iv);

    let key = this.ENCRYPTION_KEY;
    if (key.length < 32) {
      key = key.padEnd(32, "\0");
    }
    key = key.slice(0, 32);
    const keyWordArray = CryptoJS.enc.Utf8.parse(key);

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext },
      keyWordArray,
      {
        iv: ivWordArray,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      },
    );

    const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);
    if (!decryptedStr) {
      throw new Error("Decryption failed: Empty result");
    }
    return decryptedStr;
  }

  request<T = any>(config: CustomRequestConfig): Promise<ResponseModel<T>> {
    return new Promise((resolve, reject) => {
      try {
        this.service
          .request<ResponseModel<T>>(config)
          .then((res: AxiosResponse["data"]) => {
            resolve(res as ResponseModel<T>);
          })
          .catch((err) => {
            reject(err);
          });
      } catch (err) {
        reject(err);
      }
    });
  }

  get<T = any>(config: CustomRequestConfig): Promise<ResponseModel<T>> {
    return this.request({ method: "GET", ...config });
  }

  post<T = any>(config: CustomRequestConfig): Promise<ResponseModel<T>> {
    return this.request({ method: "POST", ...config });
  }

  put<T = any>(config: CustomRequestConfig): Promise<ResponseModel<T>> {
    return this.request({ method: "PUT", ...config });
  }

  patch<T = any>(config: CustomRequestConfig): Promise<ResponseModel<T>> {
    return this.request({ method: "PATCH", ...config });
  }

  delete<T = any>(config: CustomRequestConfig): Promise<ResponseModel<T>> {
    return this.request({ method: "DELETE", ...config });
  }

  upload<T = string>(fileItem: UploadFileItemModel, config?: UploadRequestConfig): Promise<ResponseModel<T>> | null {
    const formData = new FormData();
    formData.append(fileItem.name, fileItem.value);
    const nextConfig: UploadRequestConfig = config
      ? { ...config, headers: { ...(config.headers || {}), "Content-Type": "multipart/form-data" } }
      : { headers: { "Content-Type": "multipart/form-data" } };
    return this.request({
      url: "",
      data: formData,
      ...nextConfig,
    });
  }
}

const httpRequest = new HttpRequest();
export default httpRequest;
